import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@mui/material";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import ShowChartOutlinedIcon from "@mui/icons-material/ShowChartOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import { off, onValue, ref } from "firebase/database";
import { useParams } from "react-router-dom";

import { database } from "../../firebase";
import MetricCard from "../ui/MetricCard";
import RecordDialog from "../ui/RecordDialog";
import EmptyState from "../ui/EmptyState";
import Chart from "../charts/Chart";
import { baseChartOption, lineSeries, metricColor, withAlpha } from "../charts/chartTheme";
import {
  EM_DASH,
  describe,
  formatRelative,
  formatTime,
  formatTimestamp,
  latestOf,
  monthlyAverage,
} from "../../utils/format";

const HeartRate = ({ title = "Heart rate", extraActions, menu }) => {
  const { watchId } = useParams();
  const [records, setRecords] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    const heartRateRef = ref(database, `Health/${watchId}/heartRate`);

    const handler = onValue(heartRateRef, (snapshot) => {
      if (!snapshot.exists()) {
        setRecords([]);
        setSelectedRecord(null);
        return;
      }

      const parsed = Object.values(snapshot.val())
        .filter((entry) => entry?.heartRate > 0 && entry?.timestamp)
        .map((entry) => ({ ...entry, timestamp: Number(entry.timestamp) }))
        .sort((a, b) => b.timestamp - a.timestamp);

      setRecords(parsed);
      setSelectedRecord((previous) => {
        if (!previous) return parsed[0] ?? null;
        return (
          parsed.find((record) => record.timestamp === previous.timestamp) ??
          parsed[0] ??
          null
        );
      });
    });

    return () => off(heartRateRef, "value", handler);
  }, [watchId]);

  // Charted oldest-to-newest so the trend reads left to right.
  const series = useMemo(
    () => records.slice().sort((a, b) => a.timestamp - b.timestamp),
    [records]
  );

  const values = useMemo(() => series.map((r) => Number(r.heartRate)), [series]);
  const stats = useMemo(() => describe(values, 0), [values]);

  const accent = metricColor("heartRate");
  const selectedIndex = selectedRecord
    ? series.findIndex((r) => r.timestamp === selectedRecord.timestamp)
    : -1;

  const buildOption = () => {
    const color = metricColor("heartRate");
    const line = lineSeries({
      name: "Heart rate",
      data: values,
      color,
      showSymbol: values.length <= 40,
    });

    // The selected reading is marked on the trend rather than replacing it, so
    // a single record is always seen in the context of the rest.
    if (selectedIndex >= 0) {
      line.markPoint = {
        symbol: "circle",
        symbolSize: 11,
        itemStyle: { color, borderColor: "#fff", borderWidth: 2 },
        label: { show: false },
        data: [{ coord: [selectedIndex, values[selectedIndex]] }],
      };
      line.markLine = {
        silent: true,
        symbol: "none",
        lineStyle: { color: withAlpha(color, 0.4), type: [4, 4], width: 1 },
        label: { show: false },
        data: [{ xAxis: selectedIndex }],
      };
    }

    return baseChartOption({
      categories: series.map((r) => formatTime(r.timestamp)),
      series: [line],
      yName: "bpm",
      yMin: 0,
      yMax: 200,
      tooltipFormatter: (params) => {
        const point = params?.[0];
        if (!point) return "";
        return `${point.axisValue}<br/><strong>${point.value} bpm</strong>`;
      },
    });
  };

  return (
    <>
      <MetricCard
        accent={accent}
        icon={<FavoriteBorderOutlinedIcon />}
        title={title}
        meta={
          <>
            <span className="wg-chip wg-chip--accent">
              {selectedRecord ? "Record loaded" : "No records"}
            </span>
            {selectedRecord && (
              <span className="wg-chip">
                {formatTimestamp(selectedRecord.timestamp)}
              </span>
            )}
          </>
        }
        menu={menu}
        actions={
          <>
            {extraActions}
            <Button
              size="small"
              startIcon={<HistoryOutlinedIcon />}
              onClick={() => setSelectedRecord(latestOf(records))}
              disabled={!records.length}
            >
              Latest
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setDialogOpen(true)}
              disabled={!records.length}
            >
              Select record
            </Button>
          </>
        }
        note={selectedRecord?.heartRateStatusDescription}
        stats={[
          {
            label: "Selected",
            value: selectedRecord?.heartRate ?? EM_DASH,
            unit: "bpm",
            meta: selectedRecord?.displayName || "Unassigned",
          },
          {
            label: "Average",
            value: stats.avg,
            unit: "bpm",
            meta: `${stats.count} samples`,
          },
          { label: "Range", value: stats.range, unit: "bpm", meta: "Observed window" },
          {
            label: "This month",
            value: monthlyAverage(records, (r) => r.heartRate, 0),
            unit: "bpm",
            meta: `${records.length} records`,
          },
        ]}
        empty={
          !series.length ? (
            <EmptyState
              icon={<ShowChartOutlinedIcon />}
              title="No heart rate data yet"
              copy="Once the watch reports a reading, the trend, its range and the monthly average appear here."
            />
          ) : null
        }
      >
        <Chart buildOption={buildOption} height={300} deps={[values, selectedIndex]} />
      </MetricCard>

      <RecordDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSelect={(record) => {
          setSelectedRecord(record);
          setDialogOpen(false);
        }}
        records={records}
        accent={accent}
        title="Heart rate records"
        getTitle={(record) => `${record.heartRate} bpm`}
        getMeta={(record) =>
          `${formatTimestamp(record.timestamp)} · ${formatRelative(record.timestamp)}`
        }
        isSelected={(record) => selectedRecord?.timestamp === record.timestamp}
      />
    </>
  );
};

export default HeartRate;
